import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { MdClose, MdDelete, MdArrowUpward, MdArrowDownward, MdLocationOn } from 'react-icons/md';
import { toast } from 'react-toastify';
import { useRooftop, useUser } from '@store';
import { useGetAllLocationsQuery } from '../../locations/queries/getAllLocations.query.generated';
import { useGetLocationsByIdsLazyQuery } from '../queries/getLocationsByIds.query.generated';
import { useInsertRouteTemplateMutation, useUpdateRouteTemplateMutation } from '../queries/routeTemplateMutations.generated';
import { Locations } from '@gql/base-types';
import { MovePlannerLocationSelect } from '@hopdrive/feature-move-planner';
import { useReloadGuard } from '@hopdrive/sdk-app-updater/react';

interface TemplateStop {
  location_id: number;
  name: string;
  address: string;
  // Contact isn't stored on the location — denormalized onto the template stop so
  // it prefills when the template is loaded on the run builder.
  recipient_name: string;
  recipient_phone: string;
}

export interface EditableTemplate {
  id: number;
  name: string;
  stops: any; // jsonb: [{ location_id, stop_order }]
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  template?: EditableTemplate | null;
}

/** The parts of a stop that a save writes, as one comparable string. */
const stopsKey = (stops: TemplateStop[]): string =>
  JSON.stringify(stops.map(s => [s.location_id, s.recipient_name.trim(), s.recipient_phone.trim()]));

const RouteTemplateBuilderDialog: React.FC<Props> = ({ open, onClose, onSaved, template }) => {
  const rooftop = useRooftop();
  const user = useUser();
  const customerId = rooftop?.id || 0;
  const userEmail = user?.profile?.email || 'unknown';

  const [name, setName] = React.useState('');
  const [stops, setStops] = React.useState<TemplateStop[]>([]);
  const [picker, setPicker] = React.useState<Locations | null>(null);
  // Bumped after each add to remount the picker so its input text clears.
  const [pickerKey, setPickerKey] = React.useState(0);
  // What this open started from. Null while closed; `stops` stays null until an existing
  // template's locations have loaded.
  const [seed, setSeed] = React.useState<{ name: string; stops: TemplateStop[] | null } | null>(null);

  // Saved/favorite locations for this rooftop — fed to the picker (favorites first).
  const { data: locationsData, refetch: refetchLocations } = useGetAllLocationsQuery({
    variables: { customerId },
    skip: !customerId,
    fetchPolicy: 'cache-and-network',
  });
  const savedLocations = (locationsData?.locations ?? []) as (Locations | null)[];

  const [insertTemplate, { loading: inserting }] = useInsertRouteTemplateMutation();
  const [updateTemplate, { loading: updating }] = useUpdateRouteTemplateMutation();
  const [resolveLocations, { loading: resolving }] = useGetLocationsByIdsLazyQuery();
  const loading = inserting || updating;

  // Seed from an existing template (edit) or reset (create) when opened.
  React.useEffect(() => {
    if (!open) {
      setSeed(null);
      return;
    }
    if (template) {
      setName(template.name ?? '');
      const raw: any[] = Array.isArray(template.stops) ? template.stops : [];
      const ordered = [...raw].sort((a, b) => (a.stop_order ?? 0) - (b.stop_order ?? 0));
      const ids = ordered.map(s => s.location_id);
      setSeed({ name: template.name ?? '', stops: ids.length === 0 ? [] : null });
      if (ids.length === 0) {
        setStops([]);
      } else {
        resolveLocations({ variables: { ids } }).then(res => {
          const byId = new Map((res.data?.locations ?? []).map((l: any) => [l.id, l]));
          const resolved: TemplateStop[] = ordered.map(s => {
            const loc: any = byId.get(s.location_id);
            return {
              location_id: s.location_id,
              name: loc?.name || loc?.nickname || 'Unknown location',
              address: loc?.address || '',
              recipient_name: s.recipient_name || '',
              recipient_phone: s.recipient_phone || loc?.phone || '',
            };
          });
          setStops(resolved);
          setSeed(prev => prev && { ...prev, stops: resolved });
        });
      }
    } else {
      setName('');
      setStops([]);
      setSeed({ name: '', stops: [] });
    }
    setPicker(null);
  }, [open, template, resolveLocations]);

  // An app update waits while the template has unsaved changes or a save is running.
  const edited =
    seed !== null &&
    (name.trim() !== seed.name.trim() || (seed.stops !== null && stopsKey(stops) !== stopsKey(seed.stops)));
  useReloadGuard((open && edited) || loading, 'Route template');

  const handleAddDestination = (loc?: Locations | null) => {
    if (!loc) return;
    if (!loc.id) {
      toast.error('Pick a saved suggestion so the location can be referenced.');
      return;
    }
    // MovePlannerLocationSelect fires onLocationChange more than once per pick
    // (raw selection, then the resolved/created location). Dedupe inside the
    // updater so a repeat callback is a race-safe no-op rather than a toast.
    setStops(prev =>
      prev.some(s => s.location_id === loc.id)
        ? prev
        : [
            ...prev,
            {
              location_id: loc.id,
              name: loc.name || loc.nickname || 'Location',
              address: loc.address || '',
              recipient_name: '',
              recipient_phone: (loc as any).phone || '',
            },
          ],
    );
    setPicker(null);
    setPickerKey(k => k + 1); // remount picker → clears the search text
  };

  const updateStop = (index: number, field: 'recipient_name' | 'recipient_phone', value: string) => {
    setStops(prev => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };

  const move = (index: number, dir: -1 | 1) => {
    setStops(prev => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const removeStop = (index: number) => setStops(prev => prev.filter((_, i) => i !== index));

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (stops.length === 0) {
      toast.error('Add at least one destination');
      return;
    }
    const stopsJson = stops.map((s, i) => ({
      location_id: s.location_id,
      stop_order: i + 1,
      recipient_name: s.recipient_name.trim() || null,
      recipient_phone: s.recipient_phone.trim() || null,
    }));
    try {
      if (template) {
        await updateTemplate({
          variables: { id: template.id, set: { name: name.trim(), stops: stopsJson, updated_at: 'now()' } },
        });
        toast.success('Template updated');
      } else {
        await insertTemplate({
          variables: {
            template: {
              customer_id: customerId,
              product_type: 'parts_delivery',
              name: name.trim(),
              stops: stopsJson,
              created_by: userEmail,
            },
          },
        });
        toast.success('Template created');
      }
      onSaved();
      onClose();
    } catch (error: any) {
      console.error('Failed to save template:', error);
      toast.error(`Failed to save template: ${error?.message || 'Unknown error'}`);
    }
  };

  const handleClose = () => {
    if (!loading) onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              {template ? 'Edit Route Template' : 'New Route Template'}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              A reusable set of destinations for recurring runs
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size='small' disabled={loading}>
            <MdClose />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <TextField
          fullWidth size='small'
          label='Template Name'
          value={name}
          onChange={e => setName(e.target.value)}
          required
          placeholder='Tuesday Route'
          sx={{ mb: 2.5 }}
        />

        <Typography
          variant='overline'
          sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.6, mb: 1 }}
        >
          Destinations{stops.length ? ` (${stops.length})` : ''}
        </Typography>

        {resolving ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : stops.length === 0 ? (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
            No destinations yet — add the stops this route delivers to.
          </Typography>
        ) : (
          stops.map((s, idx) => (
            <Box
              key={`${s.location_id}-${idx}`}
              sx={{ p: 1, mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    minWidth: 24, height: 24, borderRadius: '50%', bgcolor: 'grey.700', color: 'common.white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12,
                  }}
                >
                  {idx + 1}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant='body2' sx={{ fontWeight: 600 }} noWrap>
                    {s.name}
                  </Typography>
                  {s.address ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                      <MdLocationOn size={12} />
                      <Typography variant='caption' noWrap title={s.address}>
                        {s.address}
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
                <Tooltip title='Move up'>
                  <span>
                    <IconButton size='small' onClick={() => move(idx, -1)} disabled={idx === 0}>
                      <MdArrowUpward size={16} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title='Move down'>
                  <span>
                    <IconButton size='small' onClick={() => move(idx, 1)} disabled={idx === stops.length - 1}>
                      <MdArrowDownward size={16} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title='Remove'>
                  <IconButton size='small' onClick={() => removeStop(idx)}>
                    <MdDelete size={16} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, pl: 4 }}>
                <TextField
                  size='small'
                  label='Contact (optional)'
                  value={s.recipient_name}
                  onChange={e => updateStop(idx, 'recipient_name', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size='small'
                  label='Phone (optional)'
                  value={s.recipient_phone}
                  onChange={e => updateStop(idx, 'recipient_phone', e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>
          ))
        )}

        <Box sx={{ mt: 1.5 }}>
          <MovePlannerLocationSelect
            key={pickerKey}
            rooftopId={customerId}
            label={stops.length ? 'Add another destination' : 'Add a destination'}
            placeholder='Search saved locations or an address…'
            helperText={
              stops.length
                ? 'Add more stops, or click Create Template when you’re done.'
                : 'Add your first stop — search a saved location or address.'
            }
            location={picker}
            locations={savedLocations}
            onLocationChange={handleAddDestination}
            onLocationFavorite={() => { refetchLocations(); }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleSave}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {loading ? 'Saving…' : template ? 'Save Changes' : 'Create Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RouteTemplateBuilderDialog;
